import { t, useLanguage, locale } from "@/lib/i18n";
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const number = (value) => value.toLocaleString(locale());

function Breakdown({ title, columns, rows }) {
  useLanguage();
  return <section className="bg-white border border-stone-200 rounded-xl overflow-hidden">
    <h2 className="font-serif text-xl p-5">{t(title)}</h2>
    <div className="overflow-x-auto max-h-96">
      <table className="w-full text-sm text-left">
        <caption className="sr-only">{t(title)}</caption>
        <thead className="bg-stone-100 sticky top-0"><tr>{columns.map(([key, label]) => <th scope="col" key={key} className="px-5 py-3">{t(label)}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index} className="border-t border-stone-100">{columns.map(([key]) => <td key={key} className="px-5 py-3">{typeof row[key] === "number" ? number(row[key]) : row[key] || t("Unspecified")}</td>)}</tr>)}</tbody>
      </table>
      {!rows.length && <p className="p-5 text-stone-500">{t("No records yet.")}</p>}
    </div>
  </section>;
}

export default function StatisticsAdmin({ embedded = false }) {
  useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const response = await api.get("/admin/statistics"); setData(response.data); }
    catch { setError("Could not load statistics. Please try again."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  const Container = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  return <Container className={embedded ? "" : "min-h-screen bg-[#FAF7F2] p-4 sm:p-6"}>
    <div className="max-w-6xl mx-auto space-y-6">
      {!embedded && <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>}
      <div className="flex items-center justify-between gap-4"><Heading className="font-serif text-3xl">{t("Site statistics")}</Heading><Button onClick={load} disabled={loading}>{loading ? t("Loading...") : t("Refresh")}</Button></div>
      {error && <p role="alert" className="text-red-700">{t(error)}</p>}
      {loading && !data && <p role="status">{t("Loading statistics...")}</p>}
      {data && <>
        <div className="grid gap-4 sm:grid-cols-3">{[["total_views", "Total site views"], ["total_users", "Total users"], ["total_members", "Total members"]].map(([key, label]) => <section key={key} className="bg-white rounded-xl border border-stone-200 p-5"><h2 className="text-stone-600">{t(label)}</h2><p className="text-4xl font-semibold text-amber-900 mt-2">{number(data[key])}</p></section>)}</div>
        <p className="text-sm text-stone-600">{t("Views count public page loads and navigation, including repeat visits, excluding admin pages. They are not unique visitors or verified human traffic. ")}{data.views_since ? t("Tracking since {0}.", { 0: new Date(data.views_since).toLocaleString(locale()) }) : t("Tracking begins with the first recorded visit.")}{t(" Users include registered administrators; members are saved family records, excluding pending additions.")}</p>
        <Breakdown title={t("Castes per religion")} columns={[["religion", "Religion"], ["castes", "Castes"]]} rows={data.religions} />
        <Breakdown title={t("Gotras and members per caste")} columns={[["religion", "Religion"], ["caste", "Caste"], ["gotras", "Gotras"], ["members", "Members"]]} rows={data.castes} />
        <Breakdown title={t("Members per gotra")} columns={[["religion", "Religion"], ["caste", "Caste"], ["gotra", "Gotra"], ["members", "Members"]]} rows={data.gotras} />
        <p className="text-sm text-stone-500">{t("Catalog entries with no members are included. Unspecified means a member has no value assigned.")}</p>
      </>}
    </div>
  </Container>;
}
